<script lang="ts" setup>
import { Modal, UserAvatar, Dropdown } from "@/components";
import {
  DocumentSpace,
  User,
  useAddCollaboratorMutation,
} from "@/graphql/operations";
import { PropType, ref, computed, reactive } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import AccessDropdown from "./AccessDropdown.vue";
import AccessUserSearch from "./AccessUserSearch.vue";

const { t } = useI18n();

const store = useStore();

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  visibleUserCount: {
    type: Number,
    required: true,
  },
  visibleUserLimit: {
    type: Number,
    required: true,
  },
});

const emits = defineEmits(["close", "refreshData", "changeState"]);
const emitClose = () => emits("close");

const searchActive = ref<boolean>(true);
const handleSearchActive = (isActive: boolean) => {
  searchActive.value = isActive;
};

const selectedUser = ref<User | null>(null);
const setSelectedUser = (user: User | null) => {
  selectedUser.value = user;
};

const defaultVisibility = "hidden";

const items = computed(() => {
  const i = [];
  i.push({
    value: "hidden",
    title: t("documentSpace.access.hideDetails.title"),
    info: "Contact details won't be displayed in the shared space",
    displayCurrentSelection: true,
  });

  if (props.visibleUserCount < props.visibleUserLimit) {
    i.push({
      value: "visible",
      title: t("documentSpace.access.displayDetails.title"),
      info: t("documentSpace.access.displayDetails.info"),
      displayCurrentSelection: true,
    });
  } else {
    i.push({
      value: "visible",
      title: t("documentSpace.access.displayDetails.title"),
      info: t("documentSpace.access.displayDetails.disabled.info"),
      disabled: true,
      displayCurrentSelection: true,
    });
  }

  return i;
});

const visibility = ref<string>(defaultVisibility);
const setVisibility = (value: string) => {
  visibility.value = value;
};

const inviteError = ref(false);
const { executeMutation, fetching: inviting } = useAddCollaboratorMutation();
const addCollaborator = async () => {
  if (!selectedUser.value || inviting.value) {
    //FIXME handle error
    return;
  }

  inviteError.value = false;
  const result = await executeMutation({
    spaceId: props.documentSpace.id,
    userId: selectedUser.value.id,
    displayContactDetails: visibility.value === "visible",
  });

  if (result.error || !result.data?.inviteToSpaceByEmail) {
    inviteError.value = true;
    return;
  }

  emits("refreshData");
  emits("changeState");
  //display flash message?
  //TODO handle error & success, display flash message
};
</script>

<template>
  <modal
    :active="true"
    :close-disabled="inviting"
    @close="emitClose"
    :scrolling="true"
    class="medium"
  >
    <template #header>
      <button
        type="button"
        class="icon_button material-symbols-outlined left"
        :aria-label="$t('back')"
        :disabled="inviting"
        @click="emits('changeState')"
      >
        keyboard_backspace
      </button>
      {{ $t("documentSpace.access.invite.title") }}
    </template>

    <template #body>
      <p v-if="inviteError" role="alert" class="save_error">
        {{ $t("error") }}
      </p>
      <access-user-search
        :inert="inviting"
        :documentSpace="documentSpace"
        @refreshData="emits('refreshData')"
        :active="searchActive"
        @stateChange="handleSearchActive"
        @change="setSelectedUser"
      >
        <template #settings v-if="selectedUser">
          <access-dropdown
            class="settings right"
            :items="items"
            :value="visibility"
            @change="setVisibility"
          />
        </template>
      </access-user-search>
    </template>

    <template #footer>
      <div class="buttons">
        <button
          type="button"
          :class="{
            primary: true,
            button: true,
            disabled: !selectedUser || inviting,
          }"
          :disabled="!selectedUser || inviting"
          @click="addCollaborator()"
        >
          {{ $t("documentSpace.access.invite.buttonText") }}
        </button>
      </div>
    </template>
  </modal>
</template>

<style scoped lang="scss">
.save_error {
  padding: 0.75em;
  color: var(--classic-error-text, #9f3a38);
  background: var(--classic-error-surface, #fff6f6);
}

.dropdown.settings {
  margin-left: 0.3em;
}

.material-symbols-outlined.left {
  margin-right: 0.3em;
}

.icon_button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
}

.icon_button:hover {
  cursor: pointer;
}
</style>
